// @flow
import React, { useEffect, useRef, type Node } from 'react';
import classnames from 'classnames';

import { ICONS } from 'Components/utils/icon/icons';

import { Backdrop } from 'Components/frameworks/backdrop/Backdrop.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './Modal.themes';

export type ModalProps = {
  ariaLabel?: string,
  ariaLabelledby?: string,
  backdropColor?: string,
  backdropOpacity?: number,
  children: Node,
  className?: string,
  closeLabel?: string,
  id?: string,
  onClose?: (e: SyntheticEvent<*>) => void,
  portalName?: string,
  size?: 'small' | 'medium' | 'large' | 'auto',
  theme?: $Keys<typeof themes>,
};

export function Modal(props: ModalProps) {
  const {
    ariaLabel,
    ariaLabelledby,
    backdropColor,
    backdropOpacity,
    children,
    className,
    closeLabel,
    id,
    onClose,
    portalName,
    size = 'medium',
    theme = 'default',
  } = props;

  const modalButtonRef: { current: ?HTMLElement } = useRef();
  const closeBtnModal = closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : closeLabel;

  useEffect(() => {
    modalButtonRef.current && modalButtonRef.current.focus();
  }, []);

  const {
    thBody,
    thBodyStyles,
    thCloseButton,
    thCloseButtonLabel,
    thCloseButtonStyles,
    thCloseIconWidth,
    thContent,
    thRoot,
    thRootStyles,
  } = themes[theme]({ size });

  return (
    <Backdrop
      ariaLabel={ariaLabel}
      ariaLabelledby={ariaLabelledby}
      bgColor={backdropColor}
      onClose={onClose}
      opacity={backdropOpacity}
      portalName={portalName}
    >
      <aside className={thRoot} id={id} style={thRootStyles}>
        <button
          className={thCloseButton}
          data-testid="Modal-close"
          onClick={onClose}
          ref={modalButtonRef}
          style={thCloseButtonStyles}
          type="button"
        >
          {closeBtnModal && <span className={thCloseButtonLabel}>{closeBtnModal}</span>}
          <Icon name={ICONS.CROSS} width={thCloseIconWidth} />
        </button>
        <div className={thBody} style={thBodyStyles}>
          <div className={classnames(className, thContent)}>{children}</div>
        </div>
      </aside>
    </Backdrop>
  );
}
